// 業務管理（bizhub）API クライアント。backend: bizhub/views/。
// 書き込みは担当者ヘッダ X-Bizhub-Staff が必須（bizhub/permissions.py）。
import { fetchWithAuth } from "./fetchApi";
import { useBizhubStore } from "@/stores/bizhubStore";

export async function bizhubFetch(endpoint, options = {}) {
  const store = useBizhubStore();
  const headers = { ...(options.headers || {}) };
  if (store.staffId) headers["X-Bizhub-Staff"] = String(store.staffId);
  return fetchWithAuth(`/bizhub${endpoint}`, { ...options, headers });
}

function qs(params = {}) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return entries.length ? `?${new URLSearchParams(entries).toString()}` : "";
}

const json = (method, body) => ({ method, body: JSON.stringify(body) });

// 担当者（作成・更新はヘッダ不要）
export const listStaff = (includeInactive = false) => bizhubFetch(`/staff/${qs({ include_inactive: includeInactive ? 1 : "" })}`);
export const createStaff = (payload) => bizhubFetch("/staff/", json("POST", payload));
export const updateStaff = (id, payload) => bizhubFetch(`/staff/${id}/`, json("PATCH", payload));

// 取引先（役割は roles: ["customer","supplier","staff"] の配列）
export const listParties = (params = {}) => bizhubFetch(`/parties/${qs(params)}`);
export const getParty = (id) => bizhubFetch(`/parties/${id}/`);
export const createParty = (payload) => bizhubFetch("/parties/", json("POST", payload));
export const updateParty = (id, payload) => bizhubFetch(`/parties/${id}/`, json("PATCH", payload));
export const listPartyJobs = (id) => bizhubFetch(`/parties/${id}/jobs/`);
export const listAddresses = (partyId, includeInactive = false) =>
  bizhubFetch(`/parties/${partyId}/addresses/${qs({ include_inactive: includeInactive ? 1 : "" })}`);
export const createAddress = (partyId, payload) => bizhubFetch(`/parties/${partyId}/addresses/`, json("POST", payload));
export const updateAddress = (partyId, addrId, payload) => bizhubFetch(`/parties/${partyId}/addresses/${addrId}/`, json("PATCH", payload));
export const listContacts = (partyId, includeInactive = false) =>
  bizhubFetch(`/parties/${partyId}/contacts/${qs({ include_inactive: includeInactive ? 1 : "" })}`);
export const createContact = (partyId, payload) => bizhubFetch(`/parties/${partyId}/contacts/`, json("POST", payload));
export const updateContact = (partyId, contactId, payload) => bizhubFetch(`/parties/${partyId}/contacts/${contactId}/`, json("PATCH", payload));
export const getCustomerProfile = (partyId) => bizhubFetch(`/parties/${partyId}/customer-profile/`);
export const putCustomerProfile = (partyId, payload) => bizhubFetch(`/parties/${partyId}/customer-profile/`, json("PUT", payload));
export const getSupplierProfile = (partyId) => bizhubFetch(`/parties/${partyId}/supplier-profile/`);
export const putSupplierProfile = (partyId, payload) => bizhubFetch(`/parties/${partyId}/supplier-profile/`, json("PUT", payload));

// 品目
export const listItems = (params = {}) => bizhubFetch(`/items/${qs(params)}`);
export const createItem = (payload) => bizhubFetch("/items/", json("POST", payload));
export const updateItem = (id, payload) => bizhubFetch(`/items/${id}/`, json("PATCH", payload));
export const getItemOverview = (itemId) => bizhubFetch(`/items/${itemId}/overview/`); // 在庫詳細（最後に数えた日・注文中・取消済みも含む履歴）
export const packingPreview = (itemId, bags) => bizhubFetch(`/items/${itemId}/packing_preview/${qs({ bags })}`);

// 袋詰めレシピ
export const listRecipes = (bagItemId) => bizhubFetch(`/packing-recipes/${qs({ bag_item: bagItemId })}`);
export const createRecipe = (payload) => bizhubFetch("/packing-recipes/", json("POST", payload));
export const updateRecipe = (id, payload) => bizhubFetch(`/packing-recipes/${id}/`, json("PATCH", payload));
export const deleteRecipe = (id) => bizhubFetch(`/packing-recipes/${id}/`, { method: "DELETE" });

// 料金表（設計 料金表-1 §5.1）。確定した版への変更は 409
export const listPriceLists = () => bizhubFetch("/price-lists/");
export const getCurrentPriceList = () => bizhubFetch("/price-lists/current/");
export const getPriceList = (id) => bizhubFetch(`/price-lists/${id}/`);
export const createPriceList = (payload) => bizhubFetch("/price-lists/", json("POST", payload));
export const updatePriceList = (id, payload) => bizhubFetch(`/price-lists/${id}/`, json("PATCH", payload));
export const deletePriceList = (id) => bizhubFetch(`/price-lists/${id}/`, { method: "DELETE" });
export const createPriceItem = (id, payload) => bizhubFetch(`/price-lists/${id}/items/`, json("POST", payload));
export const updatePriceItem = (id, itemId, payload) => bizhubFetch(`/price-lists/${id}/items/${itemId}/`, json("PATCH", payload));
export const deletePriceItem = (id, itemId) => bizhubFetch(`/price-lists/${id}/items/${itemId}/`, { method: "DELETE" });
export const updatePriceShipping = (id, payload) => bizhubFetch(`/price-lists/${id}/shipping/`, json("PATCH", payload));
export const getPriceListDiff = (id) => bizhubFetch(`/price-lists/${id}/diff/`);
export const confirmPriceList = (id) => bizhubFetch(`/price-lists/${id}/confirm/`, json("POST", {}));
export const estimatePrice = (id, cond) => bizhubFetch(`/price-lists/${id}/estimate/`, json("POST", cond));

// 在庫・伝票・棚卸
export const getDashboard = () => bizhubFetch("/dashboard/");
export const getStock = (params = {}) => bizhubFetch(`/stock/${qs(params)}`); // as_of=YYYY-MM-DD で stock_as_of（その日までの在庫）が付く
export const listMovements = (params = {}) => bizhubFetch(`/movements/${qs(params)}`);
export const getMovement = (id) => bizhubFetch(`/movements/${id}/`);
export const createMovement = (payload) => bizhubFetch("/movements/", json("POST", payload));
export const voidMovement = (id) => bizhubFetch(`/movements/${id}/void/`, json("POST", {}));
export const postStocktake = (payload) => bizhubFetch("/stocktake/", json("POST", payload));

// AI 下書き・Manual
export const listManuals = () => bizhubFetch("/manuals/");
export const listDrafts = () => bizhubFetch("/drafts/");
export const getDraft = (id) => bizhubFetch(`/drafts/${id}/`);
export const createDraft = (payload) => bizhubFetch("/drafts/", json("POST", payload));
export const saveDraft = (id, payload) => bizhubFetch(`/drafts/${id}/save/`, json("POST", payload));
export const discardDraft = (id) => bizhubFetch(`/drafts/${id}/discard/`, json("POST", {}));
export const rateDraft = (id, payload) => bizhubFetch(`/drafts/${id}/rate/`, json("POST", payload));

// メール連携（設定・取り込んだメール・やること → 在庫の片付け）と仕入れ方
export const getMailAccount = () => bizhubFetch("/mail/account/");
export const updateMailAccount = (payload) => bizhubFetch("/mail/account/", json("PATCH", payload));
export const syncMail = () => bizhubFetch("/mail/account/sync/", json("POST", {}));
export const listMailMessages = (params = {}) => bizhubFetch(`/mail/messages/${qs(params)}`);
export const getMailMessage = (id) => bizhubFetch(`/mail/messages/${id}/`);
export const ignoreMail = (id) => bizhubFetch(`/mail/messages/${id}/ignore/`, json("POST", {}));
export const unignoreMail = (id) => bizhubFetch(`/mail/messages/${id}/unignore/`, json("POST", {}));
export const resolveMail = (id, payload) => bizhubFetch(`/mail/messages/${id}/resolve/`, json("POST", payload)); // やること → 在庫の片付け
export const listSupplierItems = (params = {}) => bizhubFetch(`/supplier-items/${qs(params)}`);
export const createSupplierItem = (payload) => bizhubFetch("/supplier-items/", json("POST", payload));
export const updateSupplierItem = (id, payload) => bizhubFetch(`/supplier-items/${id}/`, json("PATCH", payload));
export const deleteSupplierItem = (id) => bizhubFetch(`/supplier-items/${id}/`, { method: "DELETE" });

// API エラー（fetchWithAuth が投げる body）を表示用の 1 文にする
export function errorMessage(e) {
  if (!e) return "通信に失敗しました";
  if (typeof e === "string") return e;
  if (e.detail) return String(e.detail);
  if (e.staff) return String(Array.isArray(e.staff) ? e.staff[0] : e.staff);
  const firstKey = Object.keys(e).find((k) => k !== "status");
  if (firstKey) {
    const v = e[firstKey];
    return `${firstKey}: ${Array.isArray(v) ? v[0] : JSON.stringify(v)}`;
  }
  return e.message || "通信に失敗しました";
}

// DRF の 400 本体（{field: [msg]}）だけを返す。通信エラー（Error インスタンス）や他のステータスは null。
export function fieldErrors(e) {
  if (!e || e instanceof Error || typeof e !== "object") return null;
  if (e.status !== undefined && e.status !== 400) return null;
  const out = {};
  for (const [k, v] of Object.entries(e)) {
    if (k === "status" || k === "detail") continue;
    out[k] = Array.isArray(v) ? v.map(String) : [String(v)];
  }
  return Object.keys(out).length ? out : null;
}

// 画面に入力欄のある項目はフィールド下に、それ以外は toast に。errors には表示対象のみ残す。
export function splitFieldErrors(e, displayedKeys) {
  const fe = fieldErrors(e);
  if (!fe) return { field: null, message: errorMessage(e) };
  const field = {};
  const rest = [];
  for (const [k, msgs] of Object.entries(fe)) {
    if (displayedKeys.includes(k)) field[k] = msgs;
    else rest.push(`${k}: ${msgs[0]}`);
  }
  return { field: Object.keys(field).length ? field : null, message: rest.length ? rest.join(" / ") : null };
}

// アシスタントの会話・評価、Feedback
export const listConversations = () => bizhubFetch("/assistant/conversations/");
export const getConversation = (id) => bizhubFetch(`/assistant/conversations/${id}/`);
export const rateAssistantMessage = (id, rating) => bizhubFetch(`/assistant/messages/${id}/rating/`, json("POST", { rating }));
export const listFeedback = (params = {}) => bizhubFetch(`/feedback/${qs(params)}`);
export const createFeedback = (payload) => bizhubFetch("/feedback/", json("POST", payload));
export const updateFeedback = (id, payload) => bizhubFetch(`/feedback/${id}/`, json("PATCH", payload));

// 案件・タスク（backend: bizhub/views/job_views.py）
export const listJobs = (params = {}) => bizhubFetch(`/jobs/${qs(params)}`);
export const getJob = (id) => bizhubFetch(`/jobs/${id}/`);
export const createJob = (payload) => bizhubFetch("/jobs/", json("POST", payload));
export const updateJob = (id, payload) => bizhubFetch(`/jobs/${id}/`, json("PATCH", payload));
export const transitionJob = (id, to, reason = "") => bizhubFetch(`/jobs/${id}/transition/`, json("POST", { to, reason }));
export const listJobEvents = (id, page = 1) => bizhubFetch(`/jobs/${id}/events/${qs({ page })}`);
export const addJobNote = (id, text) => bizhubFetch(`/jobs/${id}/notes/`, json("POST", { text }));
export const addJobTask = (id, payload) => bizhubFetch(`/jobs/${id}/tasks/`, json("POST", payload));
export const getSchedule = (from, to) => bizhubFetch(`/jobs/schedule/${qs({ from, to })}`);
export const listTasks = (params = {}) => bizhubFetch(`/tasks/${qs(params)}`);
export const updateTask = (id, payload) => bizhubFetch(`/tasks/${id}/`, json("PATCH", payload));

// 添付（S3 の presigned POST。サーバから {key, upload:{url, fields}} をもらい、fields + file を multipart POST する）
export const listAttachments = (jobId) => bizhubFetch(`/jobs/${jobId}/attachments/`);
export const signAttachment = (jobId, payload) => bizhubFetch(`/jobs/${jobId}/attachments/`, json("POST", payload));
export const completeAttachment = (jobId, payload) => bizhubFetch(`/jobs/${jobId}/attachments/complete/`, json("POST", payload));
export const deleteAttachment = (jobId, attId) => bizhubFetch(`/jobs/${jobId}/attachments/${attId}/`, { method: "DELETE" });

// 案件のメール（backend: bizhub/views/job_mail_views.py）
export const searchJobMail = (jobId, q = "") => bizhubFetch(`/jobs/${jobId}/mail/search/${qs({ q })}`);
export const listJobMailThreads = (jobId) => bizhubFetch(`/jobs/${jobId}/mail/threads/`);
export const listJobMailMessages = (jobId) => bizhubFetch(`/jobs/${jobId}/mail/messages/`); // メールタブの「取り込んだメール」（本文・添付・AI の変更つき）
export const linkJobMailThread = (jobId, payload) => bizhubFetch(`/jobs/${jobId}/mail/threads/`, json("POST", payload));
export const unlinkJobMailThread = (jobId, tid) => bizhubFetch(`/jobs/${jobId}/mail/threads/${tid}/`, { method: "DELETE" });
export const listJobWatch = (jobId) => bizhubFetch(`/jobs/${jobId}/mail/watch/`);
export const addJobWatch = (jobId, payload) => bizhubFetch(`/jobs/${jobId}/mail/watch/`, json("POST", payload));
export const updateJobWatch = (jobId, wid, payload) => bizhubFetch(`/jobs/${jobId}/mail/watch/${wid}/`, json("PATCH", payload));
export const listJobMailSuggestions = (jobId) => bizhubFetch(`/jobs/${jobId}/mail/suggestions/`);
export const acceptJobMailSuggestion = (jobId, sid, payload = {}) => bizhubFetch(`/jobs/${jobId}/mail/suggestions/${sid}/accept/`, json("POST", payload));
export const dismissJobMailSuggestion = (jobId, sid) => bizhubFetch(`/jobs/${jobId}/mail/suggestions/${sid}/dismiss/`, json("POST", {}));

// 案件の確認・削除（backend: bizhub/views/job_views.py）
export const reviewJob = (id) => bizhubFetch(`/jobs/${id}/review/`, json("POST", {}));
export const summarizeJob = (id) => bizhubFetch(`/jobs/${id}/summary/`, json("POST", {})); // 「まとめ直す」（今の状況。同期で AI を呼ぶ。返りは案件の詳細）
export const deleteJob = (id) => bizhubFetch(`/jobs/${id}/`, { method: "DELETE" });

// AI の自動更新（backend: bizhub/views/autoupdate_views.py）。読み直しは 202 で、結果は getJobAi の last_run で見る
export const getJobAi = (jobId) => bizhubFetch(`/jobs/${jobId}/ai/`);
export const rereadJobAi = (jobId) => bizhubFetch(`/jobs/${jobId}/ai/reread/`, json("POST", {}));
export const acceptJobAiSuggestion = (jobId, sid, reason = "") => bizhubFetch(`/jobs/${jobId}/ai/suggestions/${sid}/accept/`, json("POST", { reason }));
export const dismissJobAiSuggestion = (jobId, sid) => bizhubFetch(`/jobs/${jobId}/ai/suggestions/${sid}/dismiss/`, json("POST", {}));
export const undoJobAiUpdate = (jobId, eventId, comment = "") => bizhubFetch(`/jobs/${jobId}/ai/updates/${eventId}/undo/`, json("POST", { comment }));

// 機材管理（backend: bizhub/views/equipment_views.py）。状態は backend が毎回求める（status / state）
export const listEquipmentSets = (includeInactive = false) => bizhubFetch(`/equipment/sets/${qs({ include_inactive: includeInactive ? 1 : "" })}`);
export const getEquipmentSet = (id) => bizhubFetch(`/equipment/sets/${id}/`);
export const createEquipmentSet = (payload) => bizhubFetch("/equipment/sets/", json("POST", payload));
export const updateEquipmentSet = (id, payload) => bizhubFetch(`/equipment/sets/${id}/`, json("PATCH", payload));
export const assignSetMember = (setId, payload) => bizhubFetch(`/equipment/sets/${setId}/members/`, json("POST", payload));
export const endSetMember = (setId, membershipId, on) => bizhubFetch(`/equipment/sets/${setId}/members/${membershipId}/end/`, json("POST", { on }));
export const listEquipmentItems = (params = {}) => bizhubFetch(`/equipment/items/${qs(params)}`);
export const createEquipmentItem = (payload) => bizhubFetch("/equipment/items/", json("POST", payload));
export const updateEquipmentItem = (id, payload) => bizhubFetch(`/equipment/items/${id}/`, json("PATCH", payload));
export const listMaintenance = (params = {}) => bizhubFetch(`/equipment/maintenance/${qs(params)}`);
// 貸出票（backend: bizhub/views/slip_views.py。設計 F2 §5）。作成・確定・取消・入れ替えは担当者ヘッダ必須
export const getJobSlips = (jobId) => bizhubFetch(`/jobs/${jobId}/slips/`);
export const createJobSlips = (jobId, jobSetId) => bizhubFetch(`/jobs/${jobId}/slips/`, json("POST", jobSetId ? { job_set: jobSetId } : {}));
export const getSlip = (id) => bizhubFetch(`/slips/${id}/`);
export const updateSlip = (id, payload) => bizhubFetch(`/slips/${id}/`, json("PATCH", payload));
export const patchSlipLine = (slipId, lineId, payload) => bizhubFetch(`/slips/${slipId}/lines/${lineId}/`, json("PATCH", payload));
export const getSlipLineCandidates = (slipId, lineId) => bizhubFetch(`/slips/${slipId}/lines/${lineId}/candidates/`);
export const swapSlipLine = (slipId, lineId, payload) => bizhubFetch(`/slips/${slipId}/lines/${lineId}/swap/`, json("POST", payload));
export const getSlipWarnings = (id, which) => bizhubFetch(`/slips/${id}/warnings/${qs({ for: which })}`);
export const shipSlip = (id, on) => bizhubFetch(`/slips/${id}/ship/`, json("POST", on ? { on } : {}));
export const unshipSlip = (id) => bizhubFetch(`/slips/${id}/unship/`, json("POST", {}));
export const returnSlip = (id, on) => bizhubFetch(`/slips/${id}/return/`, json("POST", on ? { on } : {}));
export const unreturnSlip = (id) => bizhubFetch(`/slips/${id}/unreturn/`, json("POST", {}));
export const getSlipPrint = (id) => bizhubFetch(`/slips/${id}/print/`);
export const listSlips = (view = "upcoming") => bizhubFetch(`/slips/${qs({ view })}`);
export const listSlipTemplateLines = () => bizhubFetch("/slip-template-lines/");
export const createSlipTemplateLine = (payload) => bizhubFetch("/slip-template-lines/", json("POST", payload));
export const updateSlipTemplateLine = (id, payload) => bizhubFetch(`/slip-template-lines/${id}/`, json("PATCH", payload));
export const createMaintenance = (payload) => bizhubFetch("/equipment/maintenance/", json("POST", payload));
export const updateMaintenance = (id, payload) => bizhubFetch(`/equipment/maintenance/${id}/`, json("PATCH", payload));
export const deleteMaintenance = (id) => bizhubFetch(`/equipment/maintenance/${id}/`, { method: "DELETE" });
export const getEquipmentCalendar = (from, to) => bizhubFetch(`/equipment/calendar/${qs({ from, to })}`);
export const getEquipmentInventory = () => bizhubFetch("/equipment/inventory/");
export const postEquipmentInventory = (payload) => bizhubFetch("/equipment/inventory/", json("POST", payload));
// 案件の号機（GET は選択の候補、PUT は置き換え。返りの warnings は止めない注意）
export const getJobSets = (jobId) => bizhubFetch(`/jobs/${jobId}/sets/`);
export const putJobSets = (jobId, sets) => bizhubFetch(`/jobs/${jobId}/sets/`, json("PUT", { sets }));

// やること（設計 2026-11-03 やること §5）: お客様へのメール（Outreach）・お願い・在庫。書き込みは担当者ヘッダが要る
export const getTodo = () => bizhubFetch("/todo/");
export const getOutreach = (id) => bizhubFetch(`/todo/outreach/${id}/`);
export const dismissOutreach = (id) => bizhubFetch(`/todo/outreach/${id}/dismiss/`, json("POST", {}));
export const reopenOutreach = (id) => bizhubFetch(`/todo/outreach/${id}/reopen/`, json("POST", {}));
export const redraftOutreach = (id) => bizhubFetch(`/todo/outreach/${id}/redraft/`, json("POST", {}));

// 問い合わせの受付（backend: bizhub/views/inquiry_views.py）
export const listInquiries = (status = "open") => bizhubFetch(`/inquiries/${qs({ status })}`);
export const draftInquiry = (id) => bizhubFetch(`/inquiries/${id}/draft/`, json("POST", {}));
export const createJobFromInquiry = (id, payload) => bizhubFetch(`/inquiries/${id}/create-job/`, json("POST", payload));
export const linkInquiry = (id, jobId) => bizhubFetch(`/inquiries/${id}/link/`, json("POST", { job: jobId }));
export const dismissInquiry = (id) => bizhubFetch(`/inquiries/${id}/dismiss/`, json("POST", {}));

// board の読み取り（backend: bizhub/views/board_views.py。設計 H1 §9）。board には書き込まない（紐付け・外す・違うは bizhub の中だけ）。
// 紐付け・外すに渡すのは BoardProject の id（pk）で、board_id ではない
export const getBoardState = () => bizhubFetch("/board/");
export const syncBoard = () => bizhubFetch("/board/sync/", json("POST", {}));
export const searchBoardProjects = (params = {}) => bizhubFetch(`/board/projects/${qs(params)}`);
export const listBoardOrders = () => bizhubFetch("/board/orders/");
export const draftFromBoard = (id) => bizhubFetch(`/board/projects/${id}/draft/`, json("POST", {}));
export const createJobFromBoard = (id, payload) => bizhubFetch(`/board/projects/${id}/create-job/`, json("POST", payload));
export const linkBoardProject = (id, jobId) => bizhubFetch(`/board/projects/${id}/link/`, json("POST", { job: jobId }));
export const dismissBoardProject = (id) => bizhubFetch(`/board/projects/${id}/dismiss/`, json("POST", {}));
export const getJobBoard = (jobId) => bizhubFetch(`/jobs/${jobId}/board/`);
export const linkJobBoard = (jobId, boardProjectId) => bizhubFetch(`/jobs/${jobId}/board/link/`, json("POST", { board_project: boardProjectId }));
export const unlinkJobBoard = (jobId, boardProjectId, comment = "") =>
  bizhubFetch(`/jobs/${jobId}/board/unlink/`, json("POST", { board_project: boardProjectId, comment }));

export async function uploadToS3(upload, file) {
  const form = new FormData();
  for (const [k, v] of Object.entries(upload.fields || {})) form.append(k, v);
  form.append("file", file);
  const res = await fetch(upload.url, { method: "POST", body: form });
  if (!res.ok) throw new Error(`アップロードに失敗しました（S3 ${res.status}）`);
}
